import Component from './component';
import Hex from './hex';
import ViewBox from './view-box';

export default class SVG extends Component {
	/** The fill color of the SVG, either a Hex instance or null. */
	public set fill(value: Hex | null) {
		assertIsValidFill(value);
		this.#fill = value;
		this.#fillChanged = true;
		this.invalidateProperties();
	}

	public get fill(): Hex | null {
		return this.#fill;
	}

	/**
	 * The viewBox attribute defines the position and dimension, in user space, of an SVG viewport.
	 *
	 * The value of the viewBox attribute is a list of four numbers separated by whitespace: min-x, min-y, width, and height. min-x and min-y represent the smallest X and Y coordinates that the viewBox may have (the origin coordinates of the viewBox) and the width and height specify the viewBox size.
	 *
	 * @param value Null or an instance of ViewBox
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/viewBox} for syntax
	 */
	public set viewBox(value: null | ViewBox) {
		assertIsValidViewBox(value);
		this.#viewBox = value;
		this.#viewBoxChanged = true;
		this.invalidateProperties();
	}

	public get viewBox(): null | ViewBox {
		return this.#viewBox;
	}

	protected get svg(): SVGSVGElement {
		if (!this.#svg) {
			this.#svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
			this.#svg.style.overflow = 'visible';
			this.#svg.style.position = 'absolute';
			this.#svg.setAttribute('fill', 'none');
			this.#svg.setAttribute('width', '100%');
			this.#svg.setAttribute('height', '100%');
			this.#svg.setAttribute('preserveAspectRatio', 'none');
		}

		return this.#svg;
	}

	#fill: Hex | null;
	#fillChanged: boolean;
	#svg!: SVGSVGElement;
	#viewBox: null | ViewBox;
	#viewBoxChanged: boolean;

	public constructor() {
		super();
		this.#fill = null;
		this.#fillChanged = false;
		this.#viewBox = null;
		this.#viewBoxChanged = false;
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#viewBoxChanged) {
			this.#commitViewBox();
		}
		if (this.#fillChanged) {
			this.#commitFill();
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.appendChild(this.svg);
	}

	#commitFill(): void {
		this.#fillChanged = false;
		if (this.#fill) {
			this.svg.setAttribute('fill', this.#fill.toStyleString());
		} else {
			this.svg.setAttribute('fill', '');
		}
	}

	#commitViewBox(): void {
		this.#viewBoxChanged = false;
		if (this.#viewBox) {
			this.svg.setAttribute('viewBox', this.#viewBox.toViewBoxString());
		} else {
			this.svg.setAttribute('viewBox', '');
		}
	}
}
customElements.define('ac-svg', SVG);

export function assertIsValidFill(value: unknown): asserts value is Hex | null {
	if (value instanceof Hex || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be instance of Hex or null`);
}

function assertIsValidViewBox(value: unknown): asserts value is null | ViewBox {
	if (value === null || value instanceof ViewBox) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be null or instance of ViewBox`);
}
